import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { 
  AlertTriangle, 
  Pill, 
  FileX, 
  FlaskConical,
  ChevronDown,
  ChevronUp,
  Bell,
  Settings,
  RefreshCw,
  Building2,
  Clock,
  CheckCircle,
  Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AlertsPanel({ bases = [] }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoursThreshold, setHoursThreshold] = useState('24');
  const [filterBase, setFilterBase] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [expanded, setExpanded] = useState(false); // Minimizado por padrão

  useEffect(() => {
    if (expanded) {
      fetchAlerts();
    } else {
      fetchAlertSummary();
    }
  }, [hoursThreshold, filterBase, expanded]);

  const buildAlertParams = () => {
    const params = new URLSearchParams();
    params.append('hours_threshold', hoursThreshold);
    if (filterBase && filterBase !== 'all') {
      params.append('base_id', filterBase);
    }
    return params;
  };

  const fetchAlertSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/alerts/summary?${buildAlertParams().toString()}`);
      setSummary(response.data || { total: 0 });
      setAlerts([]);
    } catch (error) {
      console.error('Error fetching alerts summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = buildAlertParams();
      
      const [alertsRes, summaryRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/api/alerts?${params.toString()}`),
        axios.get(`${API}/api/alerts/summary?${params.toString()}`),
        axios.get(`${API}/api/milestones/alerts`)
      ]);
      
      // Combine regular alerts with milestone alerts
      const regularAlerts = alertsRes.data || [];
      const milestoneAlerts = milestonesRes.data || [];
      
      // Filter milestone alerts by base if needed
      const filteredMilestoneAlerts = filterBase === 'all' 
        ? milestoneAlerts 
        : milestoneAlerts.filter(a => a.base_id === filterBase);
      
      setAlerts([...regularAlerts, ...filteredMilestoneAlerts]);
      
      // Update summary to include milestone alerts
      const baseSummary = summaryRes.data || { total: 0 };
      baseSummary.total += filteredMilestoneAlerts.length;
      baseSummary.milestones = filteredMilestoneAlerts.length;
      setSummary(baseSummary);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'exam_out_of_range':
        return <FlaskConical className="w-4 h-4" />;
      case 'medication_overdue':
        return <Pill className="w-4 h-4" />;
      case 'lab_followup_required':
        return <FlaskConical className="w-4 h-4" />;
      case 'no_daily_record':
        return <FileX className="w-4 h-4" />;
      case 'forecast_overdue':
        return <Clock className="w-4 h-4" />;
      case 'ready_for_release':
        return <CheckCircle className="w-4 h-4" />;
      case 'no_forecast':
        return <Calendar className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high':
        return 'Alta';
      case 'medium':
        return 'Média';
      default:
        return 'Baixa';
    }
  };

  const hasAlerts = summary && summary.total > 0;

  // Minimized view - just a clickable bar
  if (!expanded) {
    return (
      <div 
        onClick={() => setExpanded(true)}
        className={`rounded-xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
          hasAlerts 
            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
            : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
        }`}
        data-testid="alerts-panel-minimized"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${hasAlerts ? 'bg-white/20' : 'bg-white/20'}`}>
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">
                {hasAlerts ? 'Animais que Precisam de Atenção' : 'Tudo em Ordem!'}
              </h3>
              {hasAlerts && summary && (
                <p className="text-sm opacity-90">
                  {summary.total} alerta{summary.total > 1 ? 's' : ''} • {summary.animals_affected} animal(is)
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasAlerts && (
              <Badge className="bg-white/20 text-white border-white/30 text-lg px-3 py-1">
                {summary?.total || 0}
              </Badge>
            )}
            <ChevronDown className="w-5 h-5 opacity-70" />
          </div>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <Card 
      className={`border-2 overflow-hidden transition-all duration-300 ${
        hasAlerts 
          ? 'border-red-300 bg-gradient-to-br from-red-50 to-orange-50' 
          : 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50'
      }`}
      data-testid="alerts-panel"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className={`w-5 h-5 ${hasAlerts ? 'text-red-600' : 'text-green-600'}`} />
            <span className={hasAlerts ? 'text-red-800' : 'text-green-800'}>
              Animais que Precisam de Atenção
            </span>
            {hasAlerts && (
              <Badge className="bg-red-600 text-white ml-2">
                {summary?.total || 0}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAlerts}
              className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(false)}
              className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div className="mt-3 p-3 bg-white rounded-lg border grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Sem registro após:</Label>
              <Select value={hoursThreshold} onValueChange={setHoursThreshold}>
                <SelectTrigger className="w-28 h-8" data-testid="hours-threshold-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 horas</SelectItem>
                  <SelectItem value="24">24 horas</SelectItem>
                  <SelectItem value="48">48 horas</SelectItem>
                  <SelectItem value="72">72 horas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Filtrar base:</Label>
              <Select value={filterBase} onValueChange={setFilterBase}>
                <SelectTrigger className="w-36 h-8" data-testid="filter-base-alerts">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as bases</SelectItem>
                  {bases.map((base) => (
                    <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : !hasAlerts ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-700 font-medium">Nenhum alerta no momento!</p>
            <p className="text-green-600 text-sm">Todos os animais estão em dia</p>
          </div>
        ) : (
          <>
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {summary.by_type.exam_out_of_range > 0 && (
                <Badge className="bg-purple-100 text-purple-800 border-purple-300 flex items-center gap-1">
                  <FlaskConical className="w-3 h-3" />
                  {summary.by_type.exam_out_of_range} exame(s)
                </Badge>
              )}
              {summary.by_type.medication_overdue > 0 && (
                <Badge className="bg-red-100 text-red-800 border-red-300 flex items-center gap-1">
                  <Pill className="w-3 h-3" />
                  {summary.by_type.medication_overdue} medicamento(s)
                </Badge>
              )}
              {summary.by_type.lab_followup_required > 0 && (
                <Badge className="bg-purple-100 text-purple-800 border-purple-300 flex items-center gap-1">
                  <FlaskConical className="w-3 h-3" />
                  {summary.by_type.lab_followup_required} exame(s)
                </Badge>
              )}
              {summary.by_type.no_daily_record > 0 && (
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 flex items-center gap-1">
                  <FileX className="w-3 h-3" />
                  {summary.by_type.no_daily_record} registro(s)
                </Badge>
              )}
              <Badge variant="outline" className="text-gray-600">
                {summary.animals_affected} animal(is)
              </Badge>
            </div>

            {/* Alerts list */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alerts.slice(0, 8).map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all ${
                    alert.severity === 'high' ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
                  }`}
                  onClick={() => navigate(alert.link)}
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      alert.severity === 'high' ? 'bg-red-200' : 'bg-yellow-200'
                    }`}>
                      {getAlertIcon(alert.alert_type)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 truncate">{alert.common_name}</span>
                        <span className="text-xs text-gray-500">({alert.ibama_id})</span>
                        <Badge className={`${getSeverityColor(alert.severity)} text-xs`} variant="outline">
                          {getSeverityLabel(alert.severity)}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{alert.title}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 rotate-[-90deg] flex-shrink-0" />
                </div>
              ))}

              {alerts.length > 8 && (
                <p className="text-center text-sm text-gray-500 pt-2">
                  + {alerts.length - 8} alertas adicionais
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
